import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { IDashboardTicketStatus } from "../type";

interface StatusCardProps {
  title: string;
  data: IDashboardTicketStatus[];
}

const StatusCard = React.memo(({ title, data }: StatusCardProps) => {
  const total = useMemo(
    () => data.reduce((acc, item) => acc + item.tickets, 0),
    [data]
  );

  const filteredData = useMemo(
    () => data.filter((item) => item.tickets > 0),
    [data]
  );

  const chartData = useMemo(
    () => ({
      series: filteredData.map((i) => i.tickets),
      labels: filteredData.map((i) => i.name),
      colors: filteredData.map((i) => i.color),
    }),
    [filteredData]
  );

  const options = useMemo(
    () => ({
      colors: chartData.colors,
      labels: chartData.labels,
      dataLabels: { enabled: false },
      stroke: { show: false },
      legend: { show: false },
      tooltip: { enabled: true },
      plotOptions: {
        pie: {
          expandOnClick: false,
          donut: {
            size: "65%",
            labels: {
              show: true,
              name: { show: false },
              total: {
                show: true,
                showAlways: true,
                label: "",
                formatter: () => `${total}`,
                fontSize: "22px",
                fontWeight: 600,
                color: "#6B7280",
              },
              value: {
                show: true,
                fontSize: "22px",
                fontWeight: 500,
                color: "#6B7280",
                offsetY: 13,
              },
            },
          },
        },
      },
    }),
    [chartData.colors, chartData.labels, total]
  );

  return (
    <div className="flex h-[116px] min-w-0 flex-1 flex-col justify-between rounded border border-gray-100 bg-white p-4">
      <span className="text-sm font-semibold text-black">{title}</span>

      {total === 0 ? (
        <div className="flex h-[62px] w-full items-center justify-center">
          <span className="text-xs text-[#9CA3AF]">
            There are no any tickets
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <div className="relative h-[62px] w-[62px] shrink-0">
            <ReactApexChart
              options={options}
              series={chartData.series}
              type="donut"
              height={62}
              width={62}
            />
          </div>

          <div className="flex flex-col gap-1 overflow-y-auto max-h-[50px] flex-1 pr-1 no-visible-bar [scrollbar-width:none]">
            {filteredData.map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-1.5 whitespace-nowrap"
              >
                <div
                  className="h-2 w-2 shrink-0 rounded-[2px]"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-[10px] font-normal text-secondary uppercase">
                  {item.name}:{" "}
                  <span className="text-secondary">{item.tickets}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

StatusCard.displayName = "StatusCard";

export default StatusCard;
